import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import styled, { useTheme } from 'styled-components';

import FancySwitch, { Switches } from '@/components/FancySwitch';
import Checkbox from '@/components/Forms/Checkbox';
import Tooltip from '@/components/InfoTooltip';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useToasts from '@/hooks/useToasts';
import { ActionButton } from '@/pages/MLProject/components/Card';
import {
  MLProjectTranslation,
  MLProjectTranslationScriptPart,
  MLProjectTranslationVoiceoverSettings,
} from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

import RangeField from './RangeField';
import VoiceField from './VoiceField';
import { OnOffButton } from './VoiceoverCard';

const Wrap = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 24px;
  border-right: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  border-left: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const Footer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
`;

const Parameters = styled.div`
  margin-top: 24px;
  padding: 24px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin: 0 0 24px;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const StyledCheckbox = styled(
  Checkbox<{ voiceoverSettings: MLProjectTranslationVoiceoverSettings }>,
)`
  font-size: 11px;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 8px;
`;

const ModelVersionSwitch = styled(FancySwitch)`
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  transition: border-color ease-out 0.2s;

  > div:first-child {
    font-size: 11px;
  }

  ${Switches} {
    &::after {
      background-color: ${({ theme }) => theme.colors.lightGray};
    }

    span {
      color: ${({ theme }) => theme.colors.gray};
    }
  }
`;

const DEFAULT_SETTINGS = {
  useSpeakerBoost: true,
  similarityBoost: 0.5,
  stability: 0.5,
  style: 0,
  modelId: 'eleven_multilingual_v2',
};

interface Props {
  translationId: number;
  settings: MLProjectTranslationVoiceoverSettings;
  partUuid: string;
  disabled: boolean;
  closeTab: () => void;
}

const VoiceoverSettings: FC<Props> = ({
  translationId,
  settings,
  partUuid,
  disabled,
  closeTab,
}) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();
  const toasts = useToasts();
  const theme = useTheme();

  const {
    control,
    setError,
    clearErrors,
    reset,
    resetField,
    watch,
    setValue,
    handleSubmit,
    formState: { isDirty, isSubmitting },
  } = useForm<{ voiceoverSettings: MLProjectTranslationVoiceoverSettings }>({
    defaultValues: { voiceoverSettings: settings },
  });

  const onSubmit = () => {
    clearErrors();
    handleSubmit(async (data) => {
      const toastId = toasts.create('loading', 'Loading...');

      try {
        const res = await api.put<object, MLProjectTranslationScriptPart>(
          `/v1/distribution/ml-translations/${translationId}/parts`,
          {
            uuid: partUuid,
            voiceoverSettings: data.voiceoverSettings,
          },
        );

        queryClient.setQueryData(
          [
            'ml_translations',
            {
              id: translationId.toString(),
            },
          ],
          (translation: MLProjectTranslation | undefined) => {
            if (!translation) {
              return undefined;
            }

            return {
              ...translation,
              script: translation.script!.map((part) => {
                if (part.uuid !== res.uuid) {
                  return part;
                }

                return res;
              }),
            };
          },
        );

        reset(data);

        closeTab();

        toasts.success(toastId, 'Saved');
      } catch (err) {
        handleFormErrors(err, setError, alerts, true);
        toasts.close(toastId);
      }
    })();
  };

  const setDefaultSettings = () => {
    resetField('voiceoverSettings.similarityBoost', {
      defaultValue: DEFAULT_SETTINGS.similarityBoost,
      keepDirty: true,
    });
    resetField('voiceoverSettings.stability', {
      defaultValue: DEFAULT_SETTINGS.stability,
      keepDirty: true,
    });
    resetField('voiceoverSettings.style', {
      defaultValue: DEFAULT_SETTINGS.style,
      keepDirty: true,
    });
    resetField('voiceoverSettings.useSpeakerBoost', {
      defaultValue: DEFAULT_SETTINGS.useSpeakerBoost,
      keepDirty: true,
    });
    resetField('voiceoverSettings.modelId', {
      defaultValue: DEFAULT_SETTINGS.modelId,
      keepDirty: true,
    });
  };

  const modelId = watch('voiceoverSettings.modelId');

  const toggleModelId = () => {
    setValue(
      'voiceoverSettings.modelId',
      modelId === 'eleven_multilingual_v2'
        ? 'eleven_turbo_v2_5'
        : 'eleven_multilingual_v2',
      {
        shouldDirty: true,
        shouldTouch: true,
      },
    );
  };

  const isSaveButtonDisabled = !isDirty || isSubmitting || disabled;

  return (
    <Wrap>
      <VoiceField control={control} />
      <Parameters>
        <Header>
          <Text $type="caption">Parameters</Text>
          <HeaderRight>
            <OnOffButton
              $large
              type="button"
              onClick={setDefaultSettings}
              disabled={disabled}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  width="24"
                  height="24"
                  rx="4"
                  fill={theme.colors.grayscale}
                />
                <path
                  d="M9.74219 10.6738H7.49219V8.42383"
                  stroke={theme.colors.gray}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14.9168 9.08318C14.5338 8.70014 14.079 8.3963 13.5786 8.189C13.0781 7.9817 12.5417 7.875 12 7.875C11.4583 7.875 10.9219 7.9817 10.4214 8.189C9.92096 8.3963 9.46622 8.70014 9.08318 9.08318L7.49219 10.6742"
                  stroke={theme.colors.gray}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14.2578 13.3262H16.5078V15.5762"
                  stroke={theme.colors.gray}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.08301 14.9172C9.46605 15.3002 9.92079 15.604 10.4213 15.8114C10.9217 16.0187 11.4581 16.1253 11.9998 16.1253C12.5415 16.1253 13.0779 16.0187 13.5784 15.8114C14.0789 15.604 14.5336 15.3002 14.9166 14.9172L16.5076 13.3262"
                  stroke={theme.colors.gray}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <Text as="span" $type="smallHeadline">
                Reset to Default
              </Text>
            </OnOffButton>
            <ModelVersionSwitch
              text="Model version:"
              isActive={modelId === 'eleven_turbo_v2_5'}
              onLabel="2.5"
              offLabel="2.0"
              onClick={toggleModelId}
            />
          </HeaderRight>
        </Header>
        <Fields>
          <RangeField<{
            voiceoverSettings: MLProjectTranslationVoiceoverSettings;
          }>
            control={control}
            label="Stability"
            name="voiceoverSettings.stability"
            minValue={0}
            step={1}
            maxValue={100}
            lowPointLabel="More variable"
            lowPointDescription="Increasing variability can make speech more expressive with output varying between re-generations. It can also lead to instabilities."
            hightPointLabel="More stable"
            highPointDescription="Increasing stability will make the voice more consistent between re-generations, but it can also make it sounds a bit monotone. On longer text fragments we recommend lowering this value."
            trackHighlight={{
              position: 'left',
              widthPercentage: 30,
            }}
          />
          <RangeField<{
            voiceoverSettings: MLProjectTranslationVoiceoverSettings;
          }>
            control={control}
            label="Clarity + Similarity Enhancement"
            name="voiceoverSettings.similarityBoost"
            minValue={0}
            step={1}
            maxValue={100}
            lowPointLabel="Low"
            lowPointDescription="Low values are recommended if background artifacts are present in generated speech."
            hightPointLabel="High"
            highPointDescription="High enhancement boosts overall voice clarity and target speaker similarity. Very high values can cause artifacts, so adjusting this setting to find the optimal value is encouraged"
          />
          <RangeField<{
            voiceoverSettings: MLProjectTranslationVoiceoverSettings;
          }>
            control={control}
            label="Style Exaggeration"
            name="voiceoverSettings.style"
            minValue={0}
            step={1}
            maxValue={100}
            lowPointLabel="None (Fastest)"
            hightPointLabel="Exaggerated"
            highPointDescription="High values are recommended if the style of the speech should be exaggerated compared to the uploaded audio. Higher values can lead to more instability in the generated speech. Setting this to 0.0 will greatly increase generation speed and is the default setting."
            trackHighlight={{
              position: 'right',
              widthPercentage: 50,
            }}
          />
          <StyledCheckbox
            control={control}
            name="voiceoverSettings.useSpeakerBoost"
          >
            Speaker Boost{' '}
            <Tooltip content="Boost the similarity of the synthesized speech and the voice at the cost of some generation speed." />
          </StyledCheckbox>
        </Fields>
      </Parameters>
      <Footer>
        <ActionButton disabled={isSaveButtonDisabled} onClick={onSubmit}>
          Save
        </ActionButton>
      </Footer>
    </Wrap>
  );
};

export default VoiceoverSettings;
